import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Plus, Search, Loader2, Save } from "lucide-react";
import { resolveImage } from "@/components/ImageUpload";

export const linesToList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
export const listToLines = (l) => (l || []).join("\n");

const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

export const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <input data-testid={testid} {...props} className={`${inputCls} mt-1`} />
  </label>
);

export const Area = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <textarea data-testid={testid} {...props} className={`${inputCls} mt-1 resize-none`} />
  </label>
);

export const SelectField = ({ label, testid, options, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <select data-testid={testid} {...props} className={`${inputCls} mt-1`}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export const TagPicker = ({ label, tags, value, onToggle, prefix }) => (
  <div>
    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <button key={t} type="button" data-testid={`${prefix}-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} onClick={() => onToggle(t)}
          className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${value.includes(t) ? "bg-emerald-700 text-white border-emerald-700" : "bg-white dark:bg-emerald-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-emerald-800"}`}>
          {t}
        </button>
      ))}
    </div>
  </div>
);

export const ActiveSwitch = ({ checked, onChange, testid }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Visible to users</span>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </div>
);

export const EditorDialog = ({ title, testid, onClose, onSave, saving, children }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-[22rem] sm:max-w-md max-h-[85vh] overflow-y-auto rounded-3xl p-5" data-testid={testid}>
      <DialogHeader><DialogTitle className="font-head text-emerald-950 dark:text-emerald-50">{title}</DialogTitle></DialogHeader>
      <div className="space-y-3 mt-1">
        {children}
        <button data-testid={`${testid}-save`} onClick={onSave} disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 rounded-2xl transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

export const ManageList = ({ title, prefix, items, query, setQuery, onAdd, onEdit, onDelete, subtitle, imageKey = "image", nameKey = "name" }) => (
  <div className="px-5 mt-5" data-testid={`${prefix}-section`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-bold text-emerald-950 dark:text-emerald-50 font-head">{title}</h3>
        <span data-testid={`${prefix}-count`} className="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-800 px-2.5 py-1 rounded-full">{items.length}</span>
      </div>
      <button data-testid={`${prefix}-add-btn`} onClick={onAdd} className="flex items-center gap-1 text-xs font-bold bg-emerald-700 text-white px-3 py-2 rounded-xl"><Plus className="w-3.5 h-3.5" /> Add</button>
    </div>
    <div className="relative mb-3">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input data-testid={`${prefix}-search`} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${title.toLowerCase()}`}
        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
    </div>
    <div className="rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 divide-y divide-slate-100 dark:divide-emerald-800">
      {items.map((it) => (
        <div key={it.id} data-testid={`${prefix}-item-${it.id}`} className="flex items-center gap-3 px-4 py-3">
          <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 bg-emerald-50 dark:bg-emerald-800 flex items-center justify-center">
            {it[imageKey] ? <img src={resolveImage(it[imageKey])} alt="" className="w-full h-full object-cover" /> : <span className="text-[9px] text-emerald-500">No img</span>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-950 dark:text-emerald-50 truncate">{it[nameKey]}</p>
            <p className="text-xs text-slate-500 truncate">{subtitle(it)} · <span className={it.active !== false ? "text-emerald-600" : "text-rose-500"}>{it.active !== false ? "Visible" : "Hidden"}</span></p>
          </div>
          <button data-testid={`${prefix}-edit-${it.id}`} onClick={() => onEdit(it)} className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
          <button data-testid={`${prefix}-delete-${it.id}`} onClick={() => onDelete(it)} className="w-9 h-9 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {items.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 text-center">Nothing here yet.</p>}
    </div>
  </div>
);
